import React, { useCallback } from 'react'
import { Avatar, Box, Flex, Spinner, Text } from '@chakra-ui/core'
import { css } from '@emotion/core'
import { ChevronLeft, ChevronRight, Layers, Tag, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useSelector } from 'react-redux'

import ContextDisplay from 'components/ContextDisplay'
import { ConmmentsCount } from 'components/TasksV2/CommentsSection/ConmmentsCount'
import useLocaleText from 'components/useLocaleText'
import { EmptyState } from 'components/ui'
import colors from 'constants/colors'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { isRtlSelector } from 'redux/selectors/general'

import { contextStatusMeta } from './meta'
import { ContextListSkeleton } from './skeletons'
import { ContextGroupMode } from './types'
import { ContextGroupSection } from './useContextGrouping'
import { ContextRow } from './useTaskDashboardData'
import LocaleText from 'components/LocaleText'
import { AiScoreTag } from 'components/TasksV2/UI/AiScoreTag'
import { StatusComposition } from 'components/TasksV2/UI/StatusComposition'
import { TASK_STATUS_ID_BY_VALUE, TASK_STATUS_LOCALE_KEY } from 'utils/taskStatusModel'

// ── Helpers ───────────────────────────────────────────────────────────────────

// A context resolves to one of the 5 task statuses (`utils/taskStatusModel`); the nuance that
// produced it (approved-with-reservation, AI review, done/approved mix) is shown by the
// `StatusComposition` block under the label instead of as its own status.
const ContextStatusText: React.FC<{ status: number }> = ({ status }) => {
  const statusId = TASK_STATUS_ID_BY_VALUE[status]
  const text = useLocaleText(statusId ? TASK_STATUS_LOCALE_KEY[statusId] : '')
  return <span>{text}</span>
}

// ── Status breakdown ──────────────────────────────────────────────────────────

type StatusBucket = { color: string; count: number }

const getStatusBuckets = (contexts: ContextRow[]): StatusBucket[] => {
  const counts: Record<string, { color: string; count: number }> = {}

  contexts.forEach((c) => {
    const meta = contextStatusMeta[c.status]
    if (!meta) return
    if (!counts[c.status]) counts[c.status] = { color: meta.bar, count: 0 }
    counts[c.status].count += 1
  })

  return Object.values(counts).filter((b) => b.count > 0)
}

const StatusBreakdown: React.FC<{ contexts: ContextRow[] }> = ({ contexts }) => {
  const buckets = getStatusBuckets(contexts)
  if (buckets.length === 0) return null

  return (
    <Flex align="center" wrap="wrap" style={{ gap: '6px' }}>
      {buckets.map((b, i) => (
        <Flex key={i} align="center" style={{ gap: '3px' }}>
          <Box w="6px" h="6px" borderRadius="50%" bg={b.color} flexShrink={0} />
          <Text fontSize="10px" fontWeight="600" color={b.color} lineHeight="1">
            {b.count}
          </Text>
        </Flex>
      ))}
    </Flex>
  )
}

const SectionCardFrame: React.FC<{
  section: ContextGroupSection
  leading: React.ReactNode
  label: React.ReactNode
  onSelectSection: (key: string) => void
}> = ({ section, leading, label, onSelectSection }) => {
  const isRtl = useSelector(isRtlSelector)
  const isEmpty = section.contexts.length === 0
  const handleClick = useCallback(() => {
    if (isEmpty) return
    onSelectSection(section.key)
  }, [isEmpty, onSelectSection, section.key])

  return (
    <Flex
      bg="white"
      borderRadius="10px"
      p="12px"
      mb="10px"
      align="center"
      justify="space-between"
      cursor={isEmpty ? 'default' : 'pointer'}
      opacity={isEmpty ? 0.5 : 1}
      boxShadow="0 1px 6px rgba(15,23,42,0.15)"
      border="1px solid #F1F5F9"
      transition="all 0.15s ease-out"
      onClick={handleClick}
      style={{ gap: '12px' }}
      css={
        isEmpty
          ? undefined
          : css`
              &:hover {
                box-shadow: 0 3px 10px rgba(15, 23, 42, 0.2);
                transform: translateY(-1px);
              }
            `
      }
    >
      {leading}

      <Box flex={1} minW={0}>
        <Text fontSize="13px" fontWeight="700" color="#0F172A" lineHeight="1.2" isTruncated>
          {label}
        </Text>
        <Box mt="5px">
          <StatusBreakdown contexts={section.contexts} />
        </Box>
      </Box>

      <Flex align="center" flexShrink={0} style={{ gap: '8px' }}>
        <Box
          bg="#EEF0F2"
          color="#475569"
          fontSize="11px"
          fontWeight="700"
          px="7px"
          py="2px"
          borderRadius="999px"
          lineHeight="1.4"
        >
          {section.contexts.length}
        </Box>
        {!isEmpty && <Box color="#CBD5E1">{isRtl ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}</Box>}
      </Flex>
    </Flex>
  )
}

// ── Section card (Groups mode) ────────────────────────────────────────────────

const GroupSectionCard: React.FC<{
  section: ContextGroupSection
  onSelectSection: (key: string) => void
}> = ({ section, onSelectSection }) => {
  const mappedRetailUsersByGroups = useSelector((state: RootState) => state.config.config.mappedRetailUsersByGroups)
  const retailUsersObject = useSelector((state: RootState) => state.config.config.retailUsersObject)

  const managerUid = mappedRetailUsersByGroups?.[section.key]?.[0]
  const managerAvatar = managerUid ? retailUsersObject?.[managerUid]?.profile_img_url : undefined

  return (
    <SectionCardFrame
      section={section}
      onSelectSection={onSelectSection}
      label={<LocaleText text={section.label} />}
      leading={
        <Box flexShrink={0}>
          {managerAvatar ? (
            <Box w="40px" h="40px" borderRadius="8px" overflow="hidden" flexShrink={0}>
              <img
                src={managerAvatar}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          ) : (
            <Flex w="40px" h="40px" borderRadius="8px" bg="#EEF0F2" align="center" justify="center" flexShrink={0}>
              <Layers size={18} color="#94A3B8" />
            </Flex>
          )}
        </Box>
      }
    />
  )
}

// ── Section card (Tags mode) ──────────────────────────────────────────────────

const TagSectionCard: React.FC<{
  section: ContextGroupSection
  onSelectSection: (key: string) => void
}> = ({ section, onSelectSection }) => {
  return (
    <SectionCardFrame
      section={section}
      onSelectSection={onSelectSection}
      label={section.label}
      leading={
        section.color ? (
          <Flex
            w="40px"
            h="40px"
            borderRadius="8px"
            bg={`${section.color}22`}
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Box w="14px" h="14px" borderRadius="4px" bg={section.color} flexShrink={0} />
          </Flex>
        ) : (
          <Flex w="40px" h="40px" borderRadius="8px" bg="#EEF0F2" align="center" justify="center" flexShrink={0}>
            <Tag size={18} color="#94A3B8" />
          </Flex>
        )
      }
    />
  )
}

// ── Section card (Auditors mode) ─────────────────────────────────────────────

const AuditorSectionCard: React.FC<{
  section: ContextGroupSection
  onSelectSection: (key: string) => void
}> = ({ section, onSelectSection }) => {
  const retailUsersObject = useSelector((state: RootState) => state.config.config.retailUsersObject)

  const auditor = retailUsersObject?.[section.key]
  const auditorAvatar = auditor?.profile_img_url || auditor?.avatar_profile_img_url

  return (
    <SectionCardFrame
      section={section}
      onSelectSection={onSelectSection}
      label={section.label}
      leading={
        <Avatar
          src={auditorAvatar}
          name={section.label}
          w="40px"
          h="40px"
          borderRadius="8px"
          flexShrink={0}
          fontSize="13px"
          fontWeight="700"
        />
      }
    />
  )
}

// ── Context row ───────────────────────────────────────────────────────────────

// ── Uncategorized section card (untagged / ungrouped) ────────────────────────

const UNCATEGORIZED_KEYS = new Set(['__untagged__', '__ungrouped__'])

const UncategorizedSectionCard: React.FC<{
  section: ContextGroupSection
  onSelectSection: (key: string) => void
}> = ({ section, onSelectSection }) => {
  const isRtl = useSelector(isRtlSelector)
  const handleClick = useCallback(() => onSelectSection(section.key), [onSelectSection, section.key])
  return (
    <Flex
      bg="#F8FAFC"
      borderRadius="10px"
      p="12px"
      mb="10px"
      align="center"
      justify="space-between"
      cursor="pointer"
      border="1px dashed #CBD5E1"
      transition="all 0.15s ease-out"
      onClick={handleClick}
      style={{ gap: '12px' }}
      css={css`
        &:hover {
          border-color: #94a3b8;
          background: #f1f5f9;
        }
      `}
    >
      <Flex w="40px" h="40px" borderRadius="8px" bg="#EEF0F2" align="center" justify="center" flexShrink={0}>
        <Tag size={16} color="#94A3B8" />
      </Flex>

      <Box flex={1} minW={0}>
        <Text fontSize="13px" fontWeight="600" color="#64748B" lineHeight="1.2" isTruncated>
          {section.label}
        </Text>
        <Box mt="5px">
          <StatusBreakdown contexts={section.contexts} />
        </Box>
      </Box>

      <Flex align="center" flexShrink={0} style={{ gap: '8px' }}>
        <Box
          bg="#E2E8F0"
          color="#64748B"
          fontSize="11px"
          fontWeight="700"
          px="7px"
          py="2px"
          borderRadius="999px"
          lineHeight="1.4"
        >
          {section.contexts.length}
        </Box>
        <Box color="#CBD5E1">{isRtl ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}</Box>
      </Flex>
    </Flex>
  )
}

// ── Context row ───────────────────────────────────────────────────────────────

const CONTEXT_ROW_AVATAR_SIZE = '38px'
// Wide enough for the status label and, on the same line, the composition block (caption + bar).
const CONTEXT_ROW_STATUS_WIDTH = '184px'
const CONTEXT_ROW_COMMENTS_WIDTH = '56px'
const CONTEXT_ROW_NAME_MAX_WIDTH = { base: '150px', md: '190px', xl: '260px' }

interface ICommentCounterInfo {
  totalCommentsCount: number
  unredCommentsCounter: number
  loading: boolean
}

interface IContextRowItemProps {
  context: ContextRow
  isLast: boolean
  openingContextId?: string | null
  isAiTask: boolean
  getCounterInfo: (context: ContextRow) => ICommentCounterInfo
  registerContextNode: (contextId: string) => (node: HTMLElement | null) => void
  onOpenContext: (contextId: string, taskContextId: string) => void
}

const ContextRowItem: React.FC<IContextRowItemProps> = ({
  context,
  isLast,
  openingContextId,
  isAiTask,
  getCounterInfo,
  registerContextNode,
  onOpenContext,
}) => {
  const isRtl = useSelector(isRtlSelector)
  const meta = contextStatusMeta[context.status] || contextStatusMeta[keys.TASK_STATUSES.TODO.value]
  const statusId = TASK_STATUS_ID_BY_VALUE[context.status]
  const commentsInfo = getCounterInfo(context)
  const isOpeningContext = openingContextId === context.contextId

  const handleClick = useCallback(() => {
    if (!isOpeningContext) onOpenContext(context.contextId, context.taskContextId)
  }, [isOpeningContext, onOpenContext, context.contextId, context.taskContextId])

  return (
    <Flex
      ref={registerContextNode(context.contextId)}
      data-context-id={context.contextId}
      bg="white"
      borderStyle="solid"
      borderTopColor="#E2E8F0"
      borderBottomColor="#E2E8F0"
      borderLeftWidth={!isRtl ? '4px' : '0px'}
      borderRightWidth={isRtl ? '4px' : '0px'}
      borderLeftColor={!isRtl ? meta.bar : colors.black}
      borderRightColor={isRtl ? meta.bar : colors.black}
      borderRadius="8px"
      p="12px"
      mb={isLast ? '0' : '10px'}
      align="center"
      justify="space-between"
      cursor={isOpeningContext ? 'default' : 'pointer'}
      opacity={isOpeningContext ? 0.72 : 1}
      pointerEvents={isOpeningContext ? 'none' : 'auto'}
      boxShadow="0 1px 6px rgba(15,23,42,0.15)"
      transition="all 0.15s ease-out"
      onClick={handleClick}
      css={css`
        &:hover {
          box-shadow: 0 3px 10px rgba(15, 23, 42, 0.2);
          transform: translateY(-1px);
        }
        &:focus {
          outline: none;
        }
      `}
    >
      <Flex align="center" minW={0} style={{ gap: '11px' }}>
        <ContextDisplay
          contextId={context.contextId}
          showGroupName
          fontSize="14px"
          fontWeight="700"
          textColor="#0F172A"
          gap={11}
          avatarProps={{ width: CONTEXT_ROW_AVATAR_SIZE, height: CONTEXT_ROW_AVATAR_SIZE, flexShrink: 0 }}
          containerProps={{ minW: 0 }}
          textProps={{ maxW: CONTEXT_ROW_NAME_MAX_WIDTH }}
        />
      </Flex>
      <Flex align="center" flexShrink={0} justify="flex-end" style={{ gap: '10px' }}>
        <Flex
          direction="column"
          align="flex-end"
          justify="center"
          minW={CONTEXT_ROW_STATUS_WIDTH}
          style={{ gap: '3px' }}
        >
          {/* Composition first, status label last — the status ends up next to the comments/chevron
              column. DOM order only: the flex row mirrors itself in RTL. (In the Hub row the
              composition goes under the status instead — that cell is narrow and tall.) */}
          <Flex align="center" justify="flex-end" style={{ gap: '10px' }}>
            {statusId && <StatusComposition taskStatus={statusId} counts={context.statusCounts} variant="context" />}
            <Flex align="center" color={meta.bar} fontSize="11px" fontWeight="800" style={{ gap: '4px' }}>
              {context.status === keys.TASK_STATUSES.APPROVED.value ? (
                <ThumbsUp size={10} />
              ) : context.status === keys.TASK_STATUSES.DISAPPROVED.value ? (
                <ThumbsDown size={10} />
              ) : (
                <Box w="6px" h="6px" borderRadius="50%" bg={meta.bar} flexShrink={0} />
              )}
              <ContextStatusText status={context.status} />
            </Flex>
          </Flex>
          {isAiTask && context.scoreDeduction != null && <AiScoreTag scoreDeduction={context.scoreDeduction} />}
        </Flex>
        <Flex align="center" justify="flex-end" w={CONTEXT_ROW_COMMENTS_WIDTH} flexShrink={0}>
          <ConmmentsCount
            totalCommentsCount={commentsInfo.totalCommentsCount}
            unredCommentsCounter={commentsInfo.unredCommentsCounter}
            loading={context.commentOids.length > 0 && commentsInfo.loading}
            pos="relative"
            left="unset"
            right="unset"
            bottom="unset"
            color={colors.black}
            fontSize="10px"
          />
        </Flex>
        {isOpeningContext ? (
          <Spinner h="18px" w="18px" borderWidth="2px" color={colors.analyticGrey} />
        ) : isRtl ? (
          <ChevronLeft size={18} />
        ) : (
          <ChevronRight size={18} />
        )}
      </Flex>
    </Flex>
  )
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ITaskPreviewContextListProps {
  isLoading: boolean
  visibleContexts: ContextRow[]
  groupMode: ContextGroupMode
  groupModeLabel: string
  groupSections: ContextGroupSection[] | null
  selectedSectionKey: string | null
  openingContextId?: string | null
  selectedStatusLabel?: string
  isRtl?: boolean
  isAiTask: boolean
  contextSearch: string
  onSelectSection: (key: string) => void
  onBack: VoidFunction
  getCounterInfo: (context: ContextRow) => ICommentCounterInfo
  registerContextNode: (contextId: string) => (node: HTMLElement | null) => void
  onOpenContext: (contextId: string, taskContextId: string, contexts: ContextRow[]) => void
}

// ── Main component ────────────────────────────────────────────────────────────

export const TaskPreviewContextList: React.FC<ITaskPreviewContextListProps> = ({
  isLoading,
  visibleContexts,
  groupMode,
  groupModeLabel,
  groupSections,
  selectedSectionKey,
  openingContextId,
  isAiTask,
  contextSearch,
  onSelectSection,
  onBack,
  getCounterInfo,
  registerContextNode,
  onOpenContext,
}) => {
  const isRtl = useSelector(isRtlSelector)
  const t_search_no_results = useLocaleText('no_assignees')
  const tNothingFound = useLocaleText('nothing_found_massage')

  // Resolve which section is active for drill-down
  const activeSection =
    groupSections && selectedSectionKey ? groupSections.find((s) => s.key === selectedSectionKey) ?? null : null

  const isSectionListMode = groupSections !== null && selectedSectionKey === null

  // Filtered sections (search by section label)
  const filteredSections =
    isSectionListMode && contextSearch
      ? groupSections!.filter((s) => s.label.toLowerCase().includes(contextSearch.toLowerCase()))
      : groupSections

  // Flat contexts to render in drill-down or flat mode (search by context name)
  const drillDownContexts = activeSection ? activeSection.contexts : visibleContexts
  const flatFiltered = contextSearch
    ? drillDownContexts.filter((c) => c.name.toLowerCase().includes(contextSearch.toLowerCase()))
    : drillDownContexts

  const emptyDefaultText = contextSearch ? t_search_no_results || 'No results found' : tNothingFound
  const emptySectionsDefaultText = contextSearch
    ? t_search_no_results || 'No results found'
    : groupMode === 'auditors'
    ? 'No auditors found'
    : groupMode === 'tags'
    ? 'No tags found'
    : 'No groups found'

  return (
    <Box flex={1} minH={0} display="flex" flexDirection="column" bg="white">
      {/* Breadcrumb — only shown when drilled into a section */}
      {activeSection && (
        <Flex
          as="button"
          align="center"
          bg="transparent"
          border="none"
          borderRadius="0"
          cursor="pointer"
          px="0"
          py="6px"
          my="4px"
          boxShadow="none"
          w="fit-content"
          maxW="100%"
          onClick={onBack}
          style={{ gap: '5px' }}
          css={css`
            &:hover .breadcrumb-arrow,
            &:hover .breadcrumb-mode {
              color: black;
            }
            &:hover .breadcrumb-label {
              color: #1e293b;
            }
            &:focus {
              outline: none;
            }
          `}
        >
          <Box
            className="breadcrumb-arrow"
            color="#94A3B8"
            display="flex"
            alignItems="center"
            style={{ transition: 'color 0.15s' }}
          >
            {isRtl ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </Box>
          <Text
            as="span"
            className="breadcrumb-mode"
            fontSize="12px"
            fontWeight="500"
            color="#94A3B8"
            lineHeight="1"
            style={{ transition: 'color 0.15s' }}
          >
            {groupModeLabel}
          </Text>
          <Box w="3px" h="3px" borderRadius="50%" bg="#CBD5E1" flexShrink={0} />
          <Text
            as="span"
            className="breadcrumb-label"
            fontSize="12px"
            fontWeight="700"
            color="#475569"
            lineHeight="1"
            isTruncated
            maxW="180px"
            style={{ transition: 'color 0.15s' }}
          >
            {activeSection.label}
          </Text>
        </Flex>
      )}

      {/* Content */}
      <Box flex={1} minH={0} overflowY="auto" pb={{ base: '10px', md: '14px' }} px="6px" mx="-6px" pt="6px">
        {isLoading && visibleContexts.length === 0 ? (
          <ContextListSkeleton />
        ) : isSectionListMode ? (
          // ── Section card list ──────────────────────────────────────────────
          !filteredSections || filteredSections.length === 0 ? (
            <EmptyState defaultText={emptySectionsDefaultText} py="32px" />
          ) : (
            <Box>
              {filteredSections.map((section) => {
                if (UNCATEGORIZED_KEYS.has(section.key)) {
                  return (
                    <UncategorizedSectionCard key={section.key} section={section} onSelectSection={onSelectSection} />
                  )
                }
                if (groupMode === 'groups') {
                  return <GroupSectionCard key={section.key} section={section} onSelectSection={onSelectSection} />
                }
                if (groupMode === 'auditors') {
                  return <AuditorSectionCard key={section.key} section={section} onSelectSection={onSelectSection} />
                }
                return <TagSectionCard key={section.key} section={section} onSelectSection={onSelectSection} />
              })}
            </Box>
          )
        ) : !isLoading && flatFiltered.length === 0 ? (
          // ── Empty ──────────────────────────────────────────────────────────
          <EmptyState defaultText={emptyDefaultText} py="32px" />
        ) : (
          // ── Flat / drill-down context list ─────────────────────────────────
          <Box>
            {flatFiltered.map((context, index) => (
              <ContextRowItem
                key={context.contextId}
                context={context}
                isLast={index === flatFiltered.length - 1}
                openingContextId={openingContextId}
                isAiTask={isAiTask}
                getCounterInfo={getCounterInfo}
                registerContextNode={registerContextNode}
                onOpenContext={(contextId, taskContextId) => onOpenContext(contextId, taskContextId, flatFiltered)}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}
